import { Component, ErrorInfo, ReactNode } from "react";
import Stars from "./Stars";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Uncaught error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage
          error={this.state.error}
          onReset={() => this.setState({ hasError: false, error: null })}
        />
      );
    }
    return this.props.children;
  }
}

function ErrorPage({ error, onReset }: { error: Error | null; onReset: () => void }) {
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
    }}>
      <Stars />
      <div style={{ position: "relative", zIndex: 1, width: "100%", display: "flex", justifyContent: "center" }}>
      <div className="glass" style={{
        maxWidth: 480,
        width: "100%",
        padding: "40px 32px",
        textAlign: "center",
        border: "1px solid rgba(255,92,92,0.2)",
      }}>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 3,
          background: "rgba(255,92,92,0.1)",
          border: "1px solid rgba(255,92,92,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
          fontSize: 22,
          color: "#FF5C5C",
        }}>
          !
        </div>

        <h1 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700, color: "#EFF2F5" }}>
          Something went wrong
        </h1>
        <p style={{ margin: "0 0 24px", color: "#98A1AB", fontSize: 14, lineHeight: 1.6 }}>
          An unexpected error occurred. You can try reloading the page or return to the home page.
        </p>

        {error?.message && (
          <div style={{
            background: "rgba(18,18,18,0.6)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 3,
            padding: "10px 14px",
            marginBottom: 24,
            textAlign: "left",
          }}>
            <p style={{ margin: 0, fontSize: 12, color: "#98A1AB", fontFamily: "monospace", wordBreak: "break-word" }}>
              {error.message}
            </p>
          </div>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={() => { onReset(); window.location.href = "/"; }}
            className="btn-primary"
            style={{ padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            Go home
          </button>
          <button
            onClick={() => window.location.reload()}
            className="btn-glass"
            style={{ padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            Reload
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
